import { GhostIconButton, Icon, getFileIcon } from "@certn/cascadia-react";
import { ActionListItem, Stack, Text } from "@certn/cascadia";
import { formatDate, formatDateTime } from "@certn/utils";
import { useTranslation } from "react-i18next";
import { Schemas } from "../../../types";
import { downloadFile } from "../../legacy-case-details/utils/legacyCaseDownloads";

type DocumentItemProps = {
  document: Schemas["CaseDocument"];
  onViewDocument: (file_url: string) => void;
};

type SignatureMetadataDetailsProps = {
  metadata: NonNullable<Schemas["CaseDocument"]["signature_metadata"]>;
};

function SignatureMetadataDetails({ metadata }: SignatureMetadataDetailsProps) {
  const { t } = useTranslation();

  return (
    <Stack gap="space-1" testID="signature-metadata">
      {metadata.signed_at && (
        <Text style="global/body-secondary" color="text-weak">
          {t("Signed {{date}}", { date: formatDateTime(metadata.signed_at) })}
        </Text>
      )}
      {metadata.signed_by_email && (
        <Text style="global/body-secondary" color="text-weak">
          {t("Signed by {{email}}", { email: metadata.signed_by_email })}
        </Text>
      )}
      {metadata.ip_address && (
        <Text style="global/body-secondary" color="text-weak">
          {t("IP address: {{ipAddress}}", { ipAddress: metadata.ip_address })}
        </Text>
      )}
      <Text style="global/body-secondary" color="text-weak">
        {t("Consent template version {{version}}", {
          version: metadata.template_version_number,
        })}
      </Text>
    </Stack>
  );
}

export function DocumentItem({ document, onViewDocument }: DocumentItemProps) {
  const file_url = document.uploaded_file.file_url;

  return (
    <ActionListItem
      titleStyle="global/heading-medium"
      title={document.type_display}
      leading={
        <Icon name={getFileIcon(document.uploaded_file.key)} size="space-6" />
      }
      trailing={
        file_url ? (
          <Stack orientation="horizontal" gap="space-2">
            <GhostIconButton
              testID="view-document"
              icon="open_in_new"
              onPress={() => {
                onViewDocument(file_url);
              }}
            />
            <GhostIconButton
              testID="download-document"
              icon="download"
              onPress={() => {
                const fileName =
                  document.uploaded_file.file_name ??
                  document.uploaded_file.key.split("/").pop() ??
                  "document";

                downloadFile(file_url, fileName);
              }}
            />
          </Stack>
        ) : null
      }
    >
      <Stack gap="space-1">
        {document.uploaded_file.file_name && (
          <Text style="global/body-secondary" color="text-weak">
            {document.uploaded_file.file_name}
          </Text>
        )}
        <Text style="global/body-secondary" color="text-weak">
          {formatDate(document.created, "MMMM dd, yyyy")}
        </Text>
        {document.signature_metadata && (
          <SignatureMetadataDetails metadata={document.signature_metadata} />
        )}
      </Stack>
    </ActionListItem>
  );
}
